import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocale } from '../../context/LocaleContext';
import {
  REGIONS,
  ALL_COUNTRY_OPTIONS,
  ENABLED_COUNTRIES,
  SUPPORTED_LANGUAGES,
  getCountriesByRegion,
  INTERNATIONAL_CODE,
} from '../../data/countries';
import Icon from '../AppIcon';

const RegionSelector = ({ compact = false }) => {
  const { countryCode, regionId, language, includeInternational, setPrefs, t, country } = useLocale();
  const [open, setOpen] = useState(false);
  const [draftRegion, setDraftRegion] = useState(regionId);
  const [draftCountry, setDraftCountry] = useState(countryCode);
  const [draftLanguage, setDraftLanguage] = useState(language);
  const [draftIncludeIntl, setDraftIncludeIntl] = useState(includeInternational);

  const filteredCountries = useMemo(
    () => getCountriesByRegion(draftRegion === 'all' ? null : draftRegion),
    [draftRegion]
  );

  const openModal = () => {
    setDraftRegion(regionId);
    setDraftCountry(countryCode);
    setDraftLanguage(language);
    setDraftIncludeIntl(includeInternational);
    setOpen(true);
  };

  const apply = () => {
    setPrefs({
      regionId: draftRegion,
      countryCode: draftCountry,
      language: draftLanguage,
      includeInternational: draftIncludeIntl,
    });
    setOpen(false);
    window.dispatchEvent(new CustomEvent('wymr:locale-changed'));
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={`flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-gray-700 ${
          compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'
        }`}
        title={t('region.title')}
      >
        <Icon name="Globe" size={compact ? 14 : 16} className="text-blue-500 shrink-0" />
        <span className="max-w-[120px] truncate font-medium">{country.name}</span>
        {!compact && (
          <span className="text-gray-400 uppercase text-xs">{language}</span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Icon name="Globe" size={20} className="text-blue-500" />
                  {t('region.title')}
                </h2>
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 p-1">
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
                {/* Region */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                    {t('region.selectRegion')}
                  </label>
                  <select
                    value={draftRegion}
                    onChange={(e) => { setDraftRegion(e.target.value); setDraftCountry(INTERNATIONAL_CODE); }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  >
                    <option value="all">{t('region.allRegions')}</option>
                    {REGIONS.map(r => (
                      <option key={r.id} value={r.id}>{t(r.labelKey)}</option>
                    ))}
                  </select>
                </div>

                {/* Country */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                    {t('region.selectCountry')}
                  </label>
                  <select
                    value={draftCountry}
                    onChange={(e) => setDraftCountry(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  >
                    <option value={INTERNATIONAL_CODE}>{t('region.international')}</option>
                    {filteredCountries.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">{filteredCountries.length} countries available</p>
                </div>

                {/* Language */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                    {t('region.selectLanguage')}
                  </label>
                  <select
                    value={draftLanguage}
                    onChange={(e) => setDraftLanguage(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  >
                    {SUPPORTED_LANGUAGES.map(l => (
                      <option key={l.code} value={l.code}>{l.nativeName} ({l.name})</option>
                    ))}
                  </select>
                </div>

                {/* Include international */}
                {draftCountry !== INTERNATIONAL_CODE && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draftIncludeIntl}
                      onChange={(e) => setDraftIncludeIntl(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('region.showGlobal')}</span>
                  </label>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
                <button
                  onClick={() => setOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={apply}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                >
                  {t('region.apply')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default RegionSelector;
